import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// SECURITY NOTE: auth tokens live in httpOnly cookies set by the backend —
// they are never stored in localStorage anymore (XSS hardening). Only the
// non-sensitive `user` profile object is cached locally for a fast first paint.
// Legacy keys (auth_token / session_token) written by older builds are still
// read once as a migration fallback, then cleaned up.
const clearLegacyTokens = () => {
  localStorage.removeItem('auth_token');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const applyAuthed = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const clearAuth = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedUser = localStorage.getItem('user');
    const legacyToken = localStorage.getItem('auth_token');
    const legacySession = localStorage.getItem('session_token');

    // Anonymous guest with no trace of a session — skip network checks.
    // (httpOnly cookies are invisible to JS, so `user` cache is our signal.)
    if (!storedUser && !legacyToken && !legacySession) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // Fast path: restore cached profile immediately, validate in background.
    if (storedUser) {
      try {
        const cached = JSON.parse(storedUser);
        setUser(cached);
        setIsAuthenticated(true);
      } catch { /* corrupt cache — ignore */ }
    }

    try {
      const response = await authAPI.getMe(); // cookie (or legacy Bearer) auth
      applyAuthed(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        // JWT cookie expired — silent refresh via httpOnly session cookie
        // (legacy localStorage session_token passed as body fallback).
        try {
          const refreshRes = await authAPI.refreshToken(legacySession || undefined);
          const { access_token, ...userData } = refreshRes.data;
          clearLegacyTokens(); // new JWT now lives in the httpOnly cookie
          applyAuthed(userData);
        } catch {
          clearAuth();
        }
      }
      // For other errors (network, 500, etc.), keep the cached session
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { user: userData } = response.data; // JWT arrives as httpOnly cookie
    applyAuthed(userData);
    // Best-effort: merge any shadow-keyed sync data into this user account
    import('../lib/syncLayer').then(m => m.mergeShadowIntoUser?.()).catch(() => {});
    return userData;
  };

  const register = async (name, email, password, role = 'player') => {
    const response = await authAPI.register({ name, email, password, role });
    const { user: userData } = response.data;
    applyAuthed(userData);
    // Best-effort: merge any shadow-keyed sync data into this user account
    import('../lib/syncLayer').then(m => m.mergeShadowIntoUser?.()).catch(() => {});
    return userData;
  };

  const loginWithGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Instant re-login: the httpOnly session cookie (or a legacy localStorage
    // session_token) can mint a fresh JWT without a Google redirect.
    const legacySession = localStorage.getItem('session_token');
    try {
      const refreshRes = await authAPI.refreshToken(legacySession || undefined);
      const { access_token, ...userData } = refreshRes.data;
      clearLegacyTokens();
      applyAuthed(userData);
      return; // Instant login — no Google redirect needed
    } catch {
      // No valid session — fall through to Google OAuth
      localStorage.removeItem('session_token');
    }
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await authAPI.exchangeSession(sessionId);
    // access_token + session_token arrive as httpOnly cookies — discard from body
    const { access_token, session_token, ...userData } = response.data;
    applyAuthed(userData);
    return userData;
  };

  const quickDemo = async () => {
    const response = await authAPI.quickDemo();
    const { user: userData } = response.data;
    applyAuthed(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout(); // backend clears the JWT cookie
    } catch (error) {
      console.error('Logout error:', error);
    }
    // The httpOnly Google session cookie is kept server-side so
    // "Continue with Google" re-logs in instantly without a redirect.
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch {}
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    loginWithGoogle,
    exchangeSession,
    quickDemo,
    logout,
    checkAuth,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
